import { cn } from '@/lib/utils';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import Translator, { useTranslation } from 'components/i18n/Translator';

import Alert from './Alert';

interface Props {
  onForgotPassword?: (email: string) => Promise<any>;
}

interface EmailFormValues {
  email: string;
}

const ProgressIndicator = ({
  step,
  totalSteps
}: {
  step: number;
  totalSteps: number;
}) => (
  <div className="flex items-center justify-center mb-6">
    <div className="flex items-center space-x-2">
      {Array.from({ length: totalSteps }, (_, i) => (
        <div key={i} className="flex items-center">
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              i < step
                ? 'bg-blue-600 text-white'
                : i === step
                ? 'bg-blue-100 text-blue-600 border-2 border-blue-600'
                : 'bg-gray-200 text-gray-400'
            }`}
          >
            {i + 1}
          </div>
          {i < totalSteps - 1 && (
            <div
              className={`w-12 h-0.5 mx-2 ${
                i < step ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            />
          )}
        </div>
      ))}
    </div>
  </div>
);

export function ForgotPasswordForm({ onForgotPassword }: Props) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState(false);
  const [rateLimited, setRateLimited] = useState(false);
  const [remainingAttempts, setRemainingAttempts] = useState<number | null>(
    null
  );
  const [userEmail, setUserEmail] = useState<string>('');

  const {
    register: registerEmail,
    handleSubmit: handleEmailSubmit,
    formState: { errors: emailErrors, touchedFields: emailTouched }
  } = useForm<EmailFormValues>({
    defaultValues: {
      email: ''
    }
  });

  const onEmailSubmit = async (data: EmailFormValues) => {
    if (!onForgotPassword) return;

    setLoading(true);
    setError('');
    setRateLimited(false);
    setRemainingAttempts(null);

    try {
      const response = await onForgotPassword(data.email);

      if (response.success) {
        setSuccess(true);
        setUserEmail(data.email);
      } else if (response.rateLimited) {
        setRateLimited(true);
        setRemainingAttempts(response.remainingAttempts || 0);
        setError(
          response.message || 'Too many requests. Please try again later.'
        );
      } else {
        setError(response.message || 'Failed to send reset link');
      }
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred');
      }
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSuccess(false);
    setError('');
    setRateLimited(false);
    setRemainingAttempts(null);
    setUserEmail('');
  };

  // Show success message after email is sent
  if (success) {
    return (
      <div className="flex flex-col gap-6">
        <ProgressIndicator step={1} totalSteps={3} />
        <div className="flex flex-col items-center gap-2 text-center">
          <h1 className="text-2xl font-bold">
            <Translator path="auth.forgotPassword.emailSentTitle" />
          </h1>
        </div>

        <Alert variant="success">
          <div className="text-center">
            <p className="text-sm">
              {t('auth.forgotPassword.magicLinkSentDescription', {
                email: userEmail
              })}
            </p>
          </div>
        </Alert>

        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={resetForm}
        >
          <Translator path="auth.forgotPassword.actions.sendAnother" />
        </Button>
      </div>
    );
  }

  // Show initial email form
  return (
    <form
      onSubmit={handleEmailSubmit(onEmailSubmit)}
      className={cn('flex flex-col gap-6')}
    >
      <ProgressIndicator step={0} totalSteps={3} />
      <div className="flex flex-col items-center gap-2 text-center">
        <h1 className="text-2xl font-bold">
          <Translator path="auth.forgotPassword.title" />
        </h1>
        <p className="text-muted-foreground">
          <Translator path="auth.forgotPassword.description" />
        </p>
      </div>

      {error && (
        <Alert variant="error">
          {error}
          {rateLimited && remainingAttempts !== null && (
            <div className="mt-2 text-sm">
              {remainingAttempts > 0
                ? `${remainingAttempts} attempts remaining`
                : 'No more attempts available. Please try again later.'}
            </div>
          )}
        </Alert>
      )}

      <div className="grid gap-6">
        <div className="grid gap-2">
          <Label htmlFor="forgot-email">
            <Translator path="auth.forgotPassword.form.email.label" />
          </Label>
          <Input
            id="forgot-email"
            type="email"
            disabled={loading}
            autoFocus
            placeholder={t('auth.forgotPassword.form.email.placeholder')}
            {...registerEmail('email', {
              required: t('auth.forgotPassword.form.email.required'),
              pattern: {
                value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                message: t('auth.forgotPassword.form.email.invalid')
              }
            })}
            className={cn(
              emailTouched.email &&
                emailErrors.email &&
                'border-black dark:border-white'
            )}
          />
          {emailTouched.email && emailErrors.email && (
            <p className="text-sm text-destructive">
              {emailErrors.email.message}
            </p>
          )}
        </div>

        <Button type="submit" className="w-full" disabled={loading}>
          {loading
            ? t('auth.forgotPassword.actions.sendingLink')
            : t('auth.forgotPassword.actions.sendLink')}
        </Button>
      </div>
    </form>
  );
}
